<template>
  <div ref="kitSummary" class="kit-summary">
    <div
      v-resize-observer="refreshLines"
      class="e-flex e-w-full e-justify-center e-relative"
    >
      <KitDetails
        ref="kitDetails"
        class="e-m-6"
        :grafana-metrics="grafanaMetrics"
        :ex-nvr-metrics="exNvrMetrics"
      />
    </div>

    <div class="e-flex e-gap-3 e-justify-center e-flex-wrap e-mt-8">
      <div
        ref="camerasContainer"
        v-resize-observer="refreshLines"
        class="kit-summary__cameras-container e-flex e-flex-col e-gap-3 e-justify-center"
        :class="{
          'e-w-full e-flex-col e-items-center position-relative':
            $vuetify.breakpoint.smAndDown,
          ' e-items-baseline': !$vuetify.breakpoint.smAndDown,
        }"
      >
        <KitCamera
          v-for="camera in kitSummaryStore.cameras"
          :key="camera.exid"
          :ref="'camera-' + camera.exid"
          :camera="camera"
          class="e-m-4"
        ></KitCamera>
      </div>

      <KitCameraPlaceholder
        ref="camera-placeholder"
        class="e-m-4 e-mt-6"
        width="150px"
        height="120px"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue"

import KitCameraPlaceholder from "@/components/kits/KitCameraPlaceholder.vue"
import KitDetails from "@/components/kits/KitDetails"
import KitCamera from "@/components/kits/KitCamera.vue"

import { mapStores } from "pinia"
import { useKitSummaryStore } from "@/stores/kitSummary"
import { useKitMetricsStore } from "@/stores/kitMetrics"
import {
  CameraStatus,
  ExNvrMetric,
  ExNvrMetricId,
  KitChartType,
  GrafanaMetricId,
  GrafanaMetricData,
} from "@evercam/shared/types"

export default Vue.extend({
  name: "KitSummary",
  components: {
    KitCameraPlaceholder,
    KitCamera,
    KitDetails,
  },
  data() {
    return {
      lines: [],
    }
  },
  computed: {
    ...mapStores(useKitSummaryStore, useKitMetricsStore),
    exNvrMetrics(): Record<ExNvrMetricId, ExNvrMetric> | null {
      if (!this.kitSummaryStore.exNvrSystemStatus) {
        return null
      }

      const { cpu, memory } = this.kitSummaryStore.exNvrSystemStatus
      const { load, numCores } = cpu
      const cpuUsage =
        (load.reduce((a, b) => a + b, 0) / load.length / numCores) * 100

      const { systemTotalMemory, availableMemory } = memory
      const ramUsed =
        ((systemTotalMemory - availableMemory) / systemTotalMemory) * 100

      return {
        [ExNvrMetricId.CpuLoad]: {
          metricId: ExNvrMetricId.CpuLoad,
          type: KitChartType.Gauge,
          value: cpuUsage,
        },
        [ExNvrMetricId.RamUsage]: {
          metricId: ExNvrMetricId.RamUsage,
          type: KitChartType.Gauge,
          value: ramUsed,
        },
      }
    },
    grafanaMetrics(): Array<Record<GrafanaMetricId, GrafanaMetricData>> {
      return this.kitMetricsStore.metricsByChartType[KitChartType.Gauge]
    },
  },
  mounted() {
    this.initConnectionLines()
  },
  beforeDestroy() {
    this.clearConnectionLines()
  },
  methods: {
    initConnectionLines() {
      this.drawConnectionLines()
      this.$addEventListener(
        "scroll",
        this.repositionConnectionLines,
        this.$refs.kitSummary as HTMLElement
      )
    },
    repositionConnectionLines() {
      try {
        this.lines.forEach((line) =>
          requestAnimationFrame(() => line.position())
        )
      } catch (e) {
        console.log("<KitSummary />: Failed to reposition leader-lines", e)
      }
    },
    drawConnectionLines() {
      const kitRef = this.$refs.kitDetails?.$refs?.kitLineAnchor as HTMLElement
      const camerasContainerRef = this.$refs.camerasContainer as HTMLElement

      if (!kitRef || !camerasContainerRef || !this.$el) {
        return
      }

      if (!window.LeaderLine) {
        this.$setTimeout(this.drawConnectionLines, 100)

        return
      }

      this.kitSummaryStore.cameras.forEach((camera) => {
        const isOnline = camera.status === CameraStatus.Online
        const color = isOnline ? "#51b031" : "rgb(229, 231, 235)"
        const animation = isOnline ? { duration: 750 } : false
        const ref = this.$refs["camera-" + camera.exid]
        const cameraEl = ref?.[0]?.$el || ref?.$el
        const line = new window.LeaderLine(cameraEl, kitRef, {
          size: 2,
          startSocket: "left",
          endSocket: "bottom",
          startPlug: "disc",
          startPlugSize: 1.5,
          endPlug: "disc",
          endPlugSize: 1.5,
          color,
          dash: { animation },
        })
        this.lines.push(line)
      })
    },
    clearConnectionLines() {
      try {
        this.lines.forEach((l) => l.remove())
        this.lines = []
      } catch (e) {
        console.log("<KitSummary />: Failed to clear leader lines", e)
      }
    },
    refreshLines() {
      this.$setTimeout(() => {
        try {
          this.clearConnectionLines()
          this.drawConnectionLines()
        } catch (e) {
          this.refreshLines()
        }
      }, 100)
    },
  },
})
</script>

<style lang="scss">
$text-color: rgb(75, 85, 99);

.kit-summary {
  flex: 1;
  overflow: auto;

  .e-card {
    .summary-element {
      min-height: 20px;
      &__label {
        &,
        &.caption {
          letter-spacing: 0 !important;
          font-weight: 500 !important;
          color: $text-color;
        }
      }
      &__content {
        color: $text-color;
        letter-spacing: 0 !important;
        white-space: normal;
        .v-btn--icon {
          height: 24px;
        }
      }
    }
  }

  &__line-anchor {
    position: absolute;
    border: 1px solid red;
    height: 1px;
    width: 1px;
    bottom: -10px;
    left: 10px;
  }

  @media (max-width: 960px) {
    .summary-element a {
      max-width: 85% !important;
      white-space: pre-line;
    }
  }
  .e-card {
    .v-chip {
      &.v-size--x-small {
        border-radius: 4px;
        font-size: 10px;
        height: 15px;
        padding: 0 7px;
        .v-chip__content {
          font-weight: bold;
        }
      }
    }
  }
}
</style>
